import React, { useEffect, useCallback } from 'react';
import { useForm, usePage } from '@inertiajs/react';
import { PublicLayout } from '../../../design-system/components/layouts';
import { Input, Textarea, Button, Alert, FormSection } from '../../../design-system/components';
import { affiliate_applications_path } from '../../../lib/routes';
const DRAFT_KEY = 'speakflow_application_draft';
function saveDraft(answers) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(answers));
  } catch {
    // localStorage unavailable — ignore
  }
}
function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}
export default function New() {
  const { questions, applicant, app_config, flash } = usePage().props;
  const appName = app_config?.company_name ?? 'Speakflow';

  // Build initial answers from questions, restoring any saved draft
  const buildInitialAnswers = useCallback(() => {
    const draft = loadDraft();
    const answers = {};
    questions.forEach((q) => {
      answers[q.id.toString()] = draft?.[q.id.toString()] ?? '';
    });
    return answers;
  }, [questions]);
  const form = useForm({
    answers: buildInitialAnswers(),
  });

  // Auto-save draft on answer changes
  useEffect(() => {
    saveDraft(form.data.answers);
  }, [form.data.answers]);
  const setAnswer = (questionId, value) => {
    form.setData('answers', {
      ...form.data.answers,
      [questionId.toString()]: value,
    });
  };
  const getError = (questionId) => {
    const errors = form.errors;
    return errors[`answers.${questionId}`] || errors[`answer_${questionId}`];
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    form.post(affiliate_applications_path(), {
      onSuccess: () => {
        clearDraft();
      },
    });
  };
  const answeredCount = Object.values(form.data.answers).filter((a) => a.trim().length > 0).length;
  const totalRequired = questions.filter((q) => q.required).length;
  const requiredAnswered = questions.filter(
    (q) => q.required && form.data.answers[q.id.toString()]?.trim(),
  ).length;
  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-semibold text-fg tracking-tight">
            Apply to the Affiliate Program
          </h1>
          <p className="mt-3 text-fg-tertiary">
            Complete the application below. We'll review it and get back to you as soon as possible.
          </p>
        </div>

        {/* Progress indicator */}
        {questions.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-fg-tertiary mb-2">
              <span>
                {answeredCount} of {questions.length} questions answered
              </span>
              {totalRequired > 0 && (
                <span>
                  {requiredAnswered} of {totalRequired} required
                </span>
              )}
            </div>
            <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{
                  width: `${questions.length > 0 ? (answeredCount / questions.length) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Error flash */}
        {flash?.alert && (
          <Alert variant="error" className="mb-6">
            {flash.alert}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-8">
            {/* Applicant info (read-only) */}
            <FormSection
              heading="Your Information"
              description={`This information comes from your ${appName} account.`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Name" value={applicant.name} disabled readOnly />
                <Input label="Email" value={applicant.email} disabled readOnly />
              </div>
            </FormSection>

            {/* Application questions */}
            {questions.length > 0 && (
              <FormSection
                heading="Application Questions"
                description="Please answer the following questions to complete your application."
              >
                <div className="space-y-5">
                  {questions.map((question, index) => (
                    <Textarea
                      key={question.id}
                      label={`${index + 1}. ${question.question_text}${question.required ? ' *' : ''}`}
                      value={form.data.answers[question.id.toString()] ?? ''}
                      onChange={(e) => setAnswer(question.id, e.target.value)}
                      error={getError(question.id)}
                      rows={3}
                    />
                  ))}
                </div>
              </FormSection>
            )}
          </div>

          {/* Submit */}
          <div className="mt-8 flex items-center justify-between">
            <p className="text-xs text-fg-tertiary">Your answers are auto-saved as you type.</p>
            <Button type="submit" variant="primary" size="lg" loading={form.processing}>
              Submit Application
            </Button>
          </div>
        </form>
      </div>
    </PublicLayout>
  );
}
